import React from 'react';
import {
  ChefHatIcon,
  CutleryIcon,
  TacoIcon,
  PizzaIcon,
  WineIcon,
  CupIcon,
  ChiliIcon,
  CupcakeIcon,
  ClocheIcon,
  PanIcon,
  IceCreamIcon,
  FlameIcon,
} from '../components/common/restaurantIcons';

/**
 * Catálogo de iconos de avatar.
 *
 * Por qué iconos y no colores: en el piso varias personas comparten el mismo
 * dispositivo y saber quién está logueado importa (quién comandó, de quién es
 * la caja). La inicial sola colisiona — dos meseros llamados Bruce dan la misma
 * letra. Un icono los distingue de un vistazo SIN gastar color, que en esta app
 * está reservado para señales semánticas (estados de mesa, montos).
 *
 * Son del mundo del restaurante y dibujados a mano (ver restaurantIcons.tsx):
 * AntD sólo tiene café, manzana, fuego, tienda y regalo de este tema, y rellenar
 * con cohetes y trofeos es justo lo genérico que este rediseño evita. Se eligieron
 * formas muy distintas entre sí para que se separen a 24-32px, que es el tamaño
 * real del avatar en el header.
 *
 * ESPEJO EXACTO de AvatarIcons.Keys en el backend
 * (Massrestaurant.Core/Constants/AvatarIcons.cs). El backend rechaza cualquier
 * clave fuera de esa lista, así que agregar un icono aquí sin agregarlo allá
 * hace que el guardado falle con 400. Al revés es inofensivo: una clave que el
 * front no conoce cae a la inicial (ver `getAvatarIcon`).
 */
export interface AvatarIconOption {
  key: string;
  label: string;
  icon: React.ReactNode;
}

export const AVATAR_ICONS: AvatarIconOption[] = [
  { key: 'chef', label: 'Gorro de chef', icon: <ChefHatIcon /> },
  { key: 'cutlery', label: 'Cubiertos', icon: <CutleryIcon /> },
  { key: 'taco', label: 'Taco', icon: <TacoIcon /> },
  { key: 'pizza', label: 'Pizza', icon: <PizzaIcon /> },
  { key: 'wine', label: 'Copa', icon: <WineIcon /> },
  { key: 'coffee', label: 'Taza', icon: <CupIcon /> },
  { key: 'chili', label: 'Chile', icon: <ChiliIcon /> },
  { key: 'cupcake', label: 'Pastelito', icon: <CupcakeIcon /> },
  { key: 'cloche', label: 'Charola', icon: <ClocheIcon /> },
  { key: 'pan', label: 'Sartén', icon: <PanIcon /> },
  { key: 'icecream', label: 'Helado', icon: <IceCreamIcon /> },
  { key: 'fire', label: 'Fuego', icon: <FlameIcon /> },
];

/**
 * Devuelve el icono de una clave, o null si no hay clave o no se reconoce.
 *
 * Tolerar la clave desconocida no es paranoia: el catálogo puede encogerse en
 * una versión futura y los usuarios que tuvieran ese icono guardado seguirían
 * con su valor en la base. Caer a la inicial es preferible a romper el header.
 */
export const getAvatarIcon = (key?: string | null): React.ReactNode | null => {
  if (!key) return null;
  return AVATAR_ICONS.find((option) => option.key === key)?.icon ?? null;
};

/**
 * Convierte a null cualquier clave que este catálogo no pueda dibujar.
 *
 * Se usa al CARGAR un valor guardado en un formulario. Mostrar la inicial cuando
 * no se reconoce la clave no basta: el valor sigue en el estado del form y se
 * reenvía al guardar, y el backend lo rechaza con 400. Pasa en cuanto el catálogo
 * cambia — de hecho ya pasó al cambiar los iconos genéricos por los de cocina.
 * Normalizar al cargar hace que el siguiente guardado limpie el dato viejo.
 */
export const sanitizeAvatarIcon = (key?: string | null): string | null =>
  getAvatarIcon(key) ? key! : null;
